import React, { useEffect, useRef } from 'react';
import { usePermission } from '../../services/PermissionService';
export interface ContextMenuState {
  x: number;
  y: number;
  type: 'user' | 'channel' | 'category' | 'channels-sidebar' | 'vault-header' | null;
  targetId: string | null;
  targetName?: string;
  visible: boolean;
}

export interface ContextMenuProps {
  contextMenu: ContextMenuState;
  activeVault: any;
  currentUser: any;
  handleMute: (id: string) => void;
  handleDeafen: (id: string) => void;
  handleKick: (id: string) => void;
  handleBan: (id: string) => void;
  handleDisconnect: (id: string) => void;
  setContextMenu: (value: any) => void;
  myPrivateVaults: any[];
  handleInvite: (userId: string, vaultId: string, vaultName: string) => void;
  setViewingProfileId: (id: string | null) => void;
  handleCreateChannelClick?: () => void;
  handleEditChannelClick?: (channelId: string) => void;
  handleDeleteChannel?: (channelId: string) => void;
  handleCreateInviteLink?: (vaultId: string) => void;
  openRoomSettings?: (vaultId?: string) => void;
  handleOpenMembersModal?: () => void;
  handleAddFriend?: (userId: string) => void;
  handleIgnoreUser?: (userId: string) => void;
  handleBlockUser?: (userId: string) => void;
  handleAddNickname?: (userId: string) => void;
  handleHideDM?: (userId: string) => void;
  handleRenameChannel?: (channelId: string, newName: string) => void;
  openChannelSettings?: (channelId: string) => void;
  handleLeaveServer?: (vaultId: string) => void;
  handleRenameCategory?: (categoryId: string, newName: string) => void;
  openCategorySettings?: (categoryId: string) => void;
  handleDeleteCategory?: (categoryId: string) => void;
  handleCreateCategoryClick?: () => void;
  onDeleteVault?: (vaultId: string) => void;
}

export const ContextMenu: React.FC<ContextMenuProps> = ({
  contextMenu,
  activeVault,
  currentUser,
  handleMute,
  handleDeafen,
  handleKick,
  handleBan,
  handleDisconnect,
  setContextMenu,
  myPrivateVaults,
  handleInvite,
  setViewingProfileId,
  handleCreateChannelClick,
  handleEditChannelClick,
  handleDeleteChannel,
  openRoomSettings,
  handleOpenMembersModal,
  handleAddFriend,
  handleIgnoreUser,
  handleBlockUser,
  handleAddNickname,
  handleHideDM,
  handleRenameChannel,
  openChannelSettings,
  handleLeaveServer,
  handleRenameCategory,
  openCategorySettings,
  handleDeleteCategory,
  handleCreateCategoryClick,
  handleCreateInviteLink,
  onDeleteVault
}) => {
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!contextMenu.visible) return;

    const handleClickOutside = (e: MouseEvent | TouchEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setContextMenu((prev: any) => ({ ...prev, visible: false }));
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setContextMenu((prev: any) => ({ ...prev, visible: false }));
      }
    };

    const timer = setTimeout(() => {
      window.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('touchstart', handleClickOutside);
      window.addEventListener('keydown', handleKeyDown);
    }, 0);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('touchstart', handleClickOutside);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [contextMenu.visible, setContextMenu]);

  // Rules of Hooks: call `usePermission` unconditionally on every render,
  // BEFORE the visibility early return below. Calling a hook after a
  // conditional `return null` flips the hook count across renders (closed
  // → 2 hooks, open → 3 hooks) which throws React error #310
  // ("Rendered more hooks than during the previous render"). Known unavoidable
  // trade-off: the hook issues a Supabase `channels` + `server_member_roles`
  // fetch whenever `activeVault.id` changes, even while the menu is closed —
  // the cost is necessary to keep the hook call order stable across the
  // closed/open render boundary. Do NOT move this back below the early return
  // as a "perf optimization" without a replacement strategy (e.g. a parent
  // gate or `eslint-plugin-react-hooks` confirming the call order).
  const canModerateUser = usePermission('KICK_MEMBERS', { vaultId: activeVault?.id });
  const canManageTargetServer = usePermission('MANAGE_SERVER', { vaultId: contextMenu.type === 'vault-header' ? contextMenu.targetId as string : (activeVault?.id || '') });

  if (!contextMenu.visible) return null;

  const isTargetCreator = contextMenu.type === 'user' ? contextMenu.targetId === activeVault?.created_by : false;

  // Viewport bounds clamping
  const menuWidth = 200;
  const menuHeight = 280;
  const left = Math.max(10, Math.min(contextMenu.x, (window.innerWidth || 1000) - menuWidth - 10));
  const top = Math.max(10, Math.min(contextMenu.y, (window.innerHeight || 800) - menuHeight - 10));

  return (
    <div 
      ref={menuRef}
      className="fixed z-[1000] bg-black/80 backdrop-blur-2xl border border-[var(--neon)]/30 rounded-xl p-2 shadow-[0_8px_32px_rgba(0,0,0,0.8)] min-w-[180px] pointer-events-auto overflow-hidden ring-1 ring-white/10 animate-in fade-in zoom-in-95 duration-100"
      style={{ top, left }}
    >
      {contextMenu.type === 'user' && (
        <>
          <button onClick={() => { setViewingProfileId(contextMenu.targetId); setContextMenu({ ...contextMenu, visible: false }); }} className="w-full text-left px-3 py-2 text-xs text-white hover:bg-white/10 rounded transition-colors">Profile</button>
          <button onClick={() => { handleAddNickname?.(contextMenu.targetId as string); setContextMenu({ ...contextMenu, visible: false }); }} className="w-full text-left px-3 py-2 text-xs text-white hover:bg-white/10 rounded transition-colors">Add Nickname</button>
          <button onClick={() => { handleAddFriend?.(contextMenu.targetId as string); setContextMenu({ ...contextMenu, visible: false }); }} className="w-full text-left px-3 py-2 text-xs text-white hover:bg-white/10 rounded transition-colors">Add friend</button>
          {handleHideDM && contextMenu.targetId && (
            <button onClick={() => { handleHideDM(contextMenu.targetId as string); setContextMenu({ ...contextMenu, visible: false }); }} className="w-full text-left px-3 py-2 text-xs text-amber-400/90 hover:bg-amber-500/10 rounded transition-colors">Hide DM</button>
          )}
          <button onClick={() => { handleIgnoreUser?.(contextMenu.targetId as string); setContextMenu({ ...contextMenu, visible: false }); }} className="w-full text-left px-3 py-2 text-xs text-white hover:bg-white/10 rounded transition-colors">Ignore</button>
          <button onClick={() => { handleBlockUser?.(contextMenu.targetId as string); setContextMenu({ ...contextMenu, visible: false }); }} className="w-full text-left px-3 py-2 text-xs text-red-500 hover:bg-red-500/10 rounded transition-colors">Block</button>

          {!isTargetCreator && myPrivateVaults.length > 0 && (
            <>
              <div className="h-[1px] bg-white/10 my-1" />
              <div className="px-3 py-1 mt-1 text-[10px] text-white/40 font-bold uppercase tracking-wider">Invite to server</div>
              {myPrivateVaults.map(v => (
                <button key={v.id} onClick={() => handleInvite(contextMenu.targetId as string, v.id, v.name)} className="w-full text-left px-3 py-2 text-xs text-white hover:bg-[var(--neon)]/10 hover:text-[var(--neon)] rounded truncate transition-colors">
                  {v.name}
                </button>
              ))}
            </>
          )}

          <div className="h-[1px] bg-white/10 my-1" />
          <button onClick={() => { navigator.clipboard.writeText(contextMenu.targetId as string); setContextMenu({ ...contextMenu, visible: false }); }} className="w-full text-left px-3 py-2 text-xs text-white/50 hover:bg-white/10 rounded transition-colors">Copy User ID</button>

          {!isTargetCreator && canModerateUser && (
            <>
              <div className="h-[1px] bg-white/10 my-1" />
              <div className="px-3 py-1 mt-1 text-[10px] text-white/40 font-bold uppercase tracking-wider">Moderation</div>
              <button onClick={() => handleMute(contextMenu.targetId as string)} className="w-full text-left px-3 py-2 text-xs text-yellow-500 hover:bg-yellow-500/10 rounded transition-colors">Mute</button>
              <button onClick={() => handleDeafen(contextMenu.targetId as string)} className="w-full text-left px-3 py-2 text-xs text-yellow-500 hover:bg-yellow-500/10 rounded transition-colors">Deafen</button>
              <button onClick={() => handleDisconnect(contextMenu.targetId as string)} className="w-full text-left px-3 py-2 text-xs text-orange-500 hover:bg-orange-500/10 rounded transition-colors">Disconnect</button>
              <button onClick={() => handleKick(contextMenu.targetId as string)} className="w-full text-left px-3 py-2 text-xs text-red-500 hover:bg-red-500/10 rounded transition-colors">Kick</button>
              <button onClick={() => handleBan(contextMenu.targetId as string)} className="w-full text-left px-3 py-2 text-xs text-red-600 hover:bg-red-600/10 rounded transition-colors">Ban</button>
            </>
          )}
        </>
      )}

      {contextMenu.type === 'channel' && (
        <>
          <div className="px-2 py-1.5">
            <input
              type="text"
              autoFocus
              className="w-full bg-black/40 border border-[var(--neon)]/30 rounded px-2 py-1 text-xs text-white outline-none focus:border-[var(--neon)]"
              defaultValue={contextMenu.targetName || ''}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                   handleRenameChannel?.(contextMenu.targetId as string, e.currentTarget.value);
                   setContextMenu({ ...contextMenu, visible: false });
                }
                if (e.key === 'Escape') {
                   setContextMenu({ ...contextMenu, visible: false });
                }
              }}
            />
          </div>
          <div className="h-[1px] bg-white/10 my-1" />
          <button 
            onClick={() => { openChannelSettings?.(contextMenu.targetId as string); setContextMenu({ ...contextMenu, visible: false }); }} 
            className="w-full text-left px-3 py-2 text-xs text-white hover:bg-white/10 rounded transition-colors"
          >
            Channel Settings...
          </button>
          <div className="h-[1px] bg-white/10 my-1" />
          <button 
            onClick={() => { handleDeleteChannel?.(contextMenu.targetId as string); setContextMenu({ ...contextMenu, visible: false }); }} 
            className="w-full text-left px-3 py-2 text-xs text-red-500 hover:bg-red-500/10 rounded transition-colors"
          >
            Delete Channel
          </button>
        </>
      )}

      {contextMenu.type === 'category' && (
        <>
          <div className="px-2 py-1.5">
            <input
              type="text"
              autoFocus
              className="w-full bg-black/40 border border-[var(--neon)]/30 rounded px-2 py-1 text-xs text-white outline-none focus:border-[var(--neon)]"
              defaultValue={contextMenu.targetName || ''}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                   handleRenameCategory?.(contextMenu.targetId as string, e.currentTarget.value);
                   setContextMenu({ ...contextMenu, visible: false });
                }
                if (e.key === 'Escape') {
                   setContextMenu({ ...contextMenu, visible: false });
                }
              }}
            />
          </div>
          <div className="h-[1px] bg-white/10 my-1" />
          <button 
            onClick={() => { openCategorySettings?.(contextMenu.targetId as string); setContextMenu({ ...contextMenu, visible: false }); }} 
            className="w-full text-left px-3 py-2 text-xs text-white hover:bg-white/10 rounded transition-colors"
          >
            Category Settings...
          </button>
          <div className="h-[1px] bg-white/10 my-1" />
          <button 
            onClick={() => { handleDeleteCategory?.(contextMenu.targetId as string); setContextMenu({ ...contextMenu, visible: false }); }} 
            className="w-full text-left px-3 py-2 text-xs text-red-500 hover:bg-red-500/10 rounded transition-colors"
          >
            Delete Category
          </button>
        </>
      )}

      {contextMenu.type === 'channels-sidebar' && (
        <>
          <button
            onClick={() => handleCreateChannelClick?.()}
            className="w-full text-left px-3 py-2 text-xs text-white hover:bg-white/10 rounded transition-colors"
          >
            Create New Channel
          </button>
          <button
            onClick={() => handleCreateCategoryClick?.()}
            className="w-full text-left px-3 py-2 text-xs text-white hover:bg-white/10 rounded transition-colors"
          >
            Create New Category
          </button>
        </>
      )}

      {contextMenu.type === 'vault-header' && (
        <>
          <button 
            onClick={() => { handleCreateInviteLink?.(contextMenu.targetId as string); setContextMenu({ ...contextMenu, visible: false }); }} 
            className="w-full text-left px-3 py-2 text-xs text-white hover:bg-white/10 rounded transition-colors"
          >
            Create Invite Link
          </button>
          {canManageTargetServer && (
            <button 
              onClick={() => { openRoomSettings?.(contextMenu.targetId as string); setContextMenu({ ...contextMenu, visible: false }); }} 
              className="w-full text-left px-3 py-2 text-xs text-white hover:bg-white/10 rounded transition-colors"
            >
              Server Settings
            </button>
          )}
          <button 
            onClick={() => { setContextMenu({ ...contextMenu, visible: false }); }} 
            className="w-full text-left px-3 py-2 text-xs text-white hover:bg-white/10 rounded transition-colors"
          >
            Mute Notifications
          </button>
          <div className="h-[1px] bg-white/10 my-1" />
          {canManageTargetServer ? (
            <button 
              onClick={() => { onDeleteVault?.(contextMenu.targetId as string); setContextMenu({ ...contextMenu, visible: false }); }} 
              className="w-full text-left px-3 py-2 text-xs text-red-500 hover:bg-red-500/10 rounded transition-colors"
            >
              Delete Server...
            </button>
          ) : (
            <button 
              onClick={() => { handleLeaveServer?.(contextMenu.targetId as string); setContextMenu({ ...contextMenu, visible: false }); }} 
              className="w-full text-left px-3 py-2 text-xs text-red-500 hover:bg-red-500/10 rounded transition-colors"
            >
              Leave Server
            </button>
          )}
        </>
      )}
    </div>
  );
};
